<template>
  <b-sidebar
    id="sidebar-terminplanung"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    bg-variant="white"
    header-class="p-0"
    width="max(58%, 840px)"
    @shown="onShow"
    @hidden="onHidden"
  >
    <template #header="{ hide }">
      <SidebarTerminplanungHeader
        :isLoading="isLoading"
        :activeReisetermin="activeReisetermin"
        :reisekuerzel="reisekuerzelKurz"
        :startdatumFormatted="startdatumFormatted"
        :disableSpeichernButton="disableSpeichernButton"
        @go-up="goUpOneRow"
        @go-down="goDownOneRow"
        @status-wechseln="$emit('statusWechseln', { reiseterminId: activeReisetermin?.id, option: $event })"
        @aufgabe-erstellen="openModalWithData"
        @termin-verschieben="$bvModal.show('reiseterminVerschiebenModal')"
        @termin-verwerfen="$emit('terminVerwerfen', { reiseterminId: activeReisetermin?.id })"
        @reisetermin-loeschen="onClickDeleteReisetermin(hide)"
        @save="saveChanges"
        @reload="onReload"
        @close="hide"
      />
    </template>
    <b-overlay :show="isLoading" style="min-height: 250px">
      <div class="sidebar-content" v-if="activeMetadata">
        <div class="sidebar-tabs">
          <button
            v-for="tab in tabs"
            v-show="tab.show"
            :key="tab.text"
            type="button"
            class="sidebar-tab"
            :class="{ 'sidebar-tab--active': activeTab === tab.text }"
            @click="activeTab = tab.text"
          >
            {{ tab.text }}
          </button>
        </div>
        <div v-if="activeTab === 'Allgemein'">
          <div class="sidebar-country-manager-bereich">
            <SidebarTerminplanungCmRueckmeldung
              ref="cmRueckmeldung"
              :bearbeitungen="cmBearbeitungen"
              :reisekuerzel="activeReisetermin.reise.reisekuerzel"
              :reiseterminState="activeReisetermin.state"
              :cmAngefragtAm="activeReisetermin.cmAngefragtAm"
              :zustaendigeCms="activeMetadata.countryManagers || []"
              :reise="activeReisetermin.reise"
              @changed="onCmRueckmeldungChanged"
            />
          </div>
          <SidebarFieldSection header="Reisedaten" v-model="reisedaten" />
          <SidebarTerminplanungCardPlanZimmerkontingente
            ref="zimmerkontingentePlan"
            class="mt-10"
            :activeMetadata="activeMetadata"
            :seasonPlanZimmerkontingente="activeReisetermin.gueltigeSaison?.planZimmerkontingente"
            :reisekuerzel="activeReisetermin.reise.reisekuerzel"
            :startdatum="activeReisetermin.startdatum"
            @changed="onZimmerkontingenteChanged"
          />
          <SidebarFieldSection
            class="mt-10"
            header="Reisetermindaten"
            v-model="reisetermindaten"
            :columns="3"
            collapsedOnMount
          />
          <SidebarFieldSection
            class="mt-10"
            header="Flugdaten"
            v-model="flugdaten"
            :country="activeReisetermin?.reiseterminkuerzel?.substring(0, 2)"
            collapsedOnMount
          />
          <SidebarTerminplanungInlandsfluege
            class="mt-10"
            :inlandsfluege="inlandsfluege"
            :activeReisetermin="activeReisetermin"
            :localInlandsflugExists="localInlandsflugExists"
            @add="addLocalInlandsflug"
            @delete="deleteInlandsflug"
          />
          <div v-if="aufgabenTerminplanung?.length > 0" class="mt-12">
            <SidebarSection title="Aufgaben" :card="false" collapsedOnMount>
              <AufgabenWidget
                :filter="'sidebar-terminplanung'"
                :ansicht="'sidebar-terminplanung'"
                :aufgaben="aufgabenTerminplanung || []"
              ></AufgabenWidget>
            </SidebarSection>
          </div>
          <!-- Kommentare -->
          <div class="mt-6">
            <ReiseterminComments
              :reiseterminId="activeReisetermin.id"
              :comments="getCommentsWithMappedAnswers"
              :hideEmojis="true"
              @updateComments="onUpdateComments"
            />
          </div>
          <ReiseterminVerschiebenModal
            :reiseterminkuerzel="activeReisetermin?.reiseterminkuerzel"
            :reiseterminId="activeReisetermin?.id"
            :reise="activeReisetermin?.reise"
            @terminVerschoben="onTerminVerschoben"
          ></ReiseterminVerschiebenModal>
        </div>
        <div v-if="activeTab === 'Verlauf'">
          <DataChangeHistory :data="verlaufData"></DataChangeHistory>
        </div>
      </div>
      <DeleteConfirmationModal
        id="modalInlandsflugLoeschen"
        title="Inlandsflug löschen?"
        entityBezeichnung="der Inlandsflug"
        @confirm="confirmDeleteInlandsflug"
      />
      <DeleteConfirmationModal
        id="modalReiseterminLoeschen"
        title="Reisetermin löschen?"
        entityBezeichnung="der Reisetermin"
        :label="activeReisetermin?.reiseterminkuerzel"
        @confirm="confirmDeleteReisetermin"
      />
    </b-overlay>
  </b-sidebar>
</template>
<script>
import { format } from 'date-fns';
import buildQuery from 'odata-query';
import { EDIT_REISETERMIN_METADATA_MULTIPLE } from '@/core/produkte/reisetermine/stores/reiseterminplanung.module';
import apiService from '@/core/common/services/api.service';
import AufgabenWidget from '@/components/produkte/reisetermine/aufgaben-widget.vue';
import SidebarSection from '@/components/common/sidebar-section/sidebar-section.vue';
import SidebarFieldSection from '@/components/common/sidebar-section/sidebar-field-section.vue';
import ReiseterminComments from '@/components/comments/reisetermin-comments.vue';
import ReiseterminVerschiebenModal from '@/components/produkte/reisetermine/reisetermin-verschieben-modal.vue';
import DataChangeHistory from '@/components/produkte/reisen/data-change-history.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import SidebarTerminplanungCardPlanZimmerkontingente from '@/components/produkte/reisetermine/sidebar-terminplanung/sidebar-terminplanung-card-plan-zimmerkontingente.vue';
import SidebarTerminplanungHeader from '@/components/produkte/reisetermine/sidebar-terminplanung/sidebar-terminplanung-header.vue';
import SidebarTerminplanungInlandsfluege from '@/components/produkte/reisetermine/sidebar-terminplanung/sidebar-terminplanung-inlandsfluege.vue';
import { buildReisedaten, buildReisetermindaten, buildFlugdaten } from './helpers/feldkonfiguration';
import { areValuesEqual } from '@/core/common/helpers/utils';
import { isInlandsflugUnchanged } from './helpers/aenderungserkennung';
import { applyRolloverRestrictions } from './helpers/rollover';
import SidebarTerminplanungCmRueckmeldung from './sidebar-terminplanung-cm-rueckmeldung.vue';
import { countryManagerKuerzelParam } from '@/core/produkte/reisetermine/reisetermin-metadata-params';

export default {
  data() {
    return {
      reisedaten: [],
      reisetermindaten: [],
      flugdaten: [],
      isLoading: false,
      inlandsfluege: null,
      prefixedVerlinkungen: [],
      activeReisetermin: null,
      activeTab: 'Allgemein',
      verlaufData: {},
      // Änderungsstand und Gültigkeit der Zimmerkontingente-Plan-Sektion
      zimmerkontingenteHasChanges: false,
      zimmerkontingenteValid: true,
      // Änderungsstand des Country-Manager-Rückmeldungs-Abschnitts
      cmHasChanges: false,
      cmValid: true,
      // Id des zu löschenden Inlandsflugs für den Bestätigungsdialog
      pendingInlandsflugId: null,
      // Schließen-Funktion der Sidebar, die nach dem Löschen des Reisetermins aufgerufen wird
      pendingReiseterminHide: null,
    };
  },
  props: {
    reiseterminId: { type: Number },
  },
  components: {
    AufgabenWidget,
    SidebarSection,
    SidebarFieldSection,
    SidebarTerminplanungCmRueckmeldung,
    ReiseterminComments,
    ReiseterminVerschiebenModal,
    DataChangeHistory,
    DeleteConfirmationModal,
    SidebarTerminplanungCardPlanZimmerkontingente,
    SidebarTerminplanungHeader,
    SidebarTerminplanungInlandsfluege,
  },
  watch: {
    // Wechselt der angezeigte Reisetermin, wird er komplett neu geladen und alle Abschnitte werden neu aufgebaut
    reiseterminId: {
      immediate: true,
      async handler(v) {
        if (v != null) {
          await this.reloadAktivenTermin();
        }
      },
    },
    activeTab() {
      this.cmHasChanges = false;
      this.zimmerkontingenteHasChanges = false;
      if (this.activeTab === 'Verlauf') {
        this.loadVerlaufFuerAktivenTermin();
      }
    },
    // Ändert sich eine Flugzeit, werden die abhängigen Rollover-Restriktionen (Übernachtung Hin/Rück) neu abgeleitet
    flugdaten: {
      deep: true,
      handler() {
        applyRolloverRestrictions(this.flugdaten);
      },
    },
  },
  computed: {
    getCommentsWithMappedAnswers() {
      if (!this.activeReisetermin || !this.activeReisetermin.comments?.length > 0) {
        return [];
      }

      const answers = this.activeReisetermin.comments.filter(comment => comment.parentId !== null);
      const commentsWithAnswers = this.activeReisetermin.comments
        .filter(comment => comment.parentId === null)
        .map(comment => {
          const matchingAnswers = answers.filter(answer => answer.parentId === comment.id);
          if (matchingAnswers.length > 0) {
            return { ...comment, answers: matchingAnswers };
          } else return { ...comment, answers: [] };
        })
        .sort((a, b) => new Date(b.createdDateUtc) - new Date(a.createdDateUtc));
      return commentsWithAnswers;
    },
    aufgabenTerminplanung() {
      if (!this.activeReisetermin || this.activeReisetermin.aufgaben?.length === 0) {
        return null;
      }

      return this.activeReisetermin.aufgaben;
    },
    gueltigeSaisonInlandsfluege() {
      if (this.activeReisetermin?.gueltigeSaison?.inlandsfluege) {
        return this.activeReisetermin.gueltigeSaison.inlandsfluege;
      } else return null;
    },
    // True, wenn kein bereits gespeicherter Inlandsflug bearbeitet wurde; nur lokale (noch nicht gespeicherte) werden ignoriert
    noInlandsflugChanges() {
      if (this.activeMetadata?.inlandsfluege?.length > 0) {
        return this.inlandsfluege.filter(inlf => !inlf?.isOnlyLocal).every(isInlandsflugUnchanged);
      } else return true;
    },
    // Liefert die bearbeiteten, bereits gespeicherten Inlandsflüge
    inlandsfluegeChanged() {
      return this.inlandsfluege.filter(inlf => !inlf?.isOnlyLocal && !isInlandsflugUnchanged(inlf));
    },
    // True, wenn weder die Reisetermindaten noch die TH Langstrecke geändert wurden
    noReiseterminDataChanges() {
      return (
        this.reisetermindaten
          .filter(item => item.editable)
          .every(item => areValuesEqual(item.value, item.valueEdited)) &&
        this.flugdaten
          .filter(item => (!Array.isArray(item.value) && item.editable) || item.editable?.[0])
          .filter(item => item.label?.[0] === 'TH Langstrecke')
          .every(item => areValuesEqual(item.value, item.valueEdited))
      );
    },
    localInlandsflug() {
      if (this.localInlandsflugExists) {
        return this.inlandsfluege.find(inlf => inlf?.isOnlyLocal);
      } else return null;
    },
    noReadyLocalInlandsflug() {
      if (this.localInlandsflug) {
        const allFieldsSet =
          this.localInlandsflug.tag !== null &&
          this.localInlandsflug.einkauf !== null &&
          this.localInlandsflug.abflughafenIataCode !== null &&
          this.localInlandsflug.abflughafenIataCode?.length > 0 &&
          this.localInlandsflug.zielflughafenIataCode !== null &&
          this.localInlandsflug.zielflughafenIataCode?.length > 0 &&
          ((this.localInlandsflug.fruehesterAbflug !== null &&
            this.localInlandsflug.spaetesteAnkunft !== null) ||
            this.localInlandsflug.minimaleUmsteigezeit !== null);
        return !allFieldsSet;
      }
      return true;
    },
    // Der Speichern-Button ist nur aktiv, wenn irgendein Bereich speicherbare Änderungen hat
    disableSpeichernButton() {
      return (
        this.noMetadataChanges &&
        this.noReiseterminDataChanges &&
        this.noInlandsflugChanges &&
        this.noReadyLocalInlandsflug &&
        this.noZimmerkontingenteChanges &&
        this.noCmChanges
      );
    },
    cmBearbeitungen() {
      return (this.activeReisetermin?.countryManagerBearbeitungen ?? [])
        .filter(bearbeitung => bearbeitung.isDeleted !== true)
        .slice()
        .sort((a, b) => new Date(a.createdDateUtc) - new Date(b.createdDateUtc));
    },
    // Ein ausgewählter Alternativtermin des CM enabled den zentralen Speichern-Button
    noCmChanges() {
      return !(this.cmHasChanges && this.cmValid);
    },
    // Nur gültige Änderungen an den Zimmerkontingenten enablen den zentralen Speichern-Button
    noZimmerkontingenteChanges() {
      return !(this.zimmerkontingenteHasChanges && this.zimmerkontingenteValid);
    },
    // True, wenn an den Reisedaten und den Flugdaten (außer TH Langstrecke) nichts geändert wurde
    noMetadataChanges() {
      return (
        this.reisedaten
          .filter(item => (!Array.isArray(item.editable) && item.editable) || item.editable?.[0])
          .every(item => areValuesEqual(item.value, item.valueEdited)) &&
        this.flugdaten
          .filter(
            item =>
              ((!Array.isArray(item.value) && item.editable) || item.editable?.[0]) &&
              item.label?.[0] !== 'TH Langstrecke'
          )
          .every(item => areValuesEqual(item.value, item.valueEdited))
      );
    },
    reisekuerzelKurz() {
      return this.activeReisetermin?.reiseterminkuerzel?.substring(0, 6) ?? '';
    },
    startdatumFormatted() {
      if (!this.activeReisetermin?.startdatum) {
        return '';
      }
      return format(new Date(this.activeReisetermin.startdatum), 'dd.MM.yyyy');
    },
    activeMetadata() {
      if (this.activeReisetermin) {
        return this.activeReisetermin.metadata;
      } else return null;
    },
    localInlandsflugExists() {
      return this.inlandsfluege?.some(inlandsflug => inlandsflug?.isOnlyLocal == true);
    },
    tabs() {
      return [
        { show: true, text: 'Allgemein' },
        { show: true, text: 'Verlauf' },
      ];
    },
  },
  methods: {
    async reloadReisetermin() {
      if (this.reiseterminId == null) {
        return;
      }
      await this.loadFullReisetermin(this.reiseterminId);
      this.rebuildAbschnitte();
    },
    async reloadAktivenTermin() {
      this.verlaufData = {};
      await this.reloadReisetermin();
      this.cmHasChanges = false;

      if (this.activeTab === 'Verlauf') {
        this.loadVerlaufFuerAktivenTermin();
      }
    },
    onReload() {
      this.reloadAktivenTermin();
      this.$emit('reload');
    },
    // Baut Inlandsflüge und die Feld-Konfigurationen der drei editierbaren Abschnitte neu auf
    rebuildAbschnitte() {
      this.initInlandsfluegeAndReiseterminData();
      this.reisedaten = buildReisedaten(this.activeReisetermin);
      this.reisetermindaten = buildReisetermindaten(this.activeReisetermin);
      this.flugdaten = buildFlugdaten(this.activeReisetermin, this.flugdaten);
    },
    loadVerlaufFuerAktivenTermin() {
      const { id: reiseterminId, reiseId, startdatum } = this.activeReisetermin;
      this.loadVerlauf(reiseterminId, startdatum, reiseId);
    },
    onTerminVerschoben() {
      return this.reloadReisetermin();
    },
    onUpdateComments(res) {
      this.activeReisetermin.comments = res.comments;
    },
    onZimmerkontingenteChanged({ hasChanges, isValid }) {
      this.zimmerkontingenteHasChanges = hasChanges;
      this.zimmerkontingenteValid = isValid;
    },
    onCmRueckmeldungChanged({ hasChanges, isValid }) {
      this.cmHasChanges = hasChanges;
      this.cmValid = isValid;
    },
    async loadFullReisetermin(id) {
      const odataQuery = buildQuery({
        filter: [{ id: { eq: id } }],
        expand: {
          reise: {},
          flugeinkauf: {},
          gueltigerSaisonpreis: {},
          gueltigeSaison: {
            expand: {
              ziellandZielflughaefen: {},
              ziellandAbflughaefen: {},
              Inlandsfluege: {},
              PlanZimmerkontingente: {},
              WeiterePartner: { select: 'countryManagerKuerzel' },
            },
          },
          metadata: {
            expand: {
              ziellandZielflughaefen: {},
              ziellandAbflughaefen: {},
              Inlandsfluege: {},
              PlanZimmerkontingente: {},
              CountryManagers: { select: 'countryManagerKuerzel' },
              WeiterePartner: { select: 'countryManagerKuerzel' },
            },
          },
          verworfenData: {},
          countryManagerBearbeitungen: {},
          aufgaben: {},
          comments: {},
          ursprungsreisetermin: {},
          ersatztermin: {},
        },
      });
      this.isLoading = true;
      try {
        const response = await apiService.get('/Reisetermin', odataQuery);
        this.activeReisetermin = response.data.result.value[0];
      } finally {
        this.isLoading = false;
      }
    },
    deleteCollapseFromURL() {
      const queryParameters = { ...this.$route.query };
      if ('collapse' in queryParameters) {
        delete queryParameters['collapse'];
        this.$router.replace({ query: queryParameters });
      }
    },
    addLocalInlandsflug() {
      if (!this.localInlandsflugExists) {
        this.inlandsfluege.unshift({
          isOnlyLocal: true,
          id: 999999,
          abflughafenIataCode: null,
          zielflughafenIataCode: null,
          tag: null,
          einkauf: null,
          fruehesterAbflug: null,
          spaetesteAnkunft: null,
          minimaleUmsteigezeit: null,
          valuesBeforeEdit: {
            abflughafenIataCode: null,
            zielflughafenIataCode: null,
            tag: null,
            einkauf: null,
            fruehesterAbflug: null,
            spaetesteAnkunft: null,
            minimaleUmsteigezeit: null,
          },
        });
      }
    },
    deleteInlandsflug(id) {
      // wenn es nur ein lokaler ist, entferne ihn
      if (id === 999999) {
        this.inlandsfluege = this.inlandsfluege.filter(inlf => inlf.id !== id);
        return;
      }

      this.pendingInlandsflugId = id;
      this.$bvModal.show('modalInlandsflugLoeschen');
    },
    confirmDeleteInlandsflug() {
      this.$bvModal.hide('modalInlandsflugLoeschen');

      const id = this.pendingInlandsflugId;
      if (id == null) return;
      this.pendingInlandsflugId = null;

      const { startdatum, reise } = this.activeReisetermin;

      this.isLoading = true;

      apiService
        .delete(
          `Reise/${reise.reisekuerzel}/metadata/${startdatum}/inlandsflug/reisetermin?inlandsflugId=${id}`
        )
        .then(() => {
          this.toast('Erfolgreich gelöscht');
          this.inlandsfluege = this.inlandsfluege.filter(inlf => inlf.id !== id);
          this.$emit('deletedInlandsflug', {
            reiseterminkuerzel: this.activeReisetermin.reiseterminkuerzel,
            id,
          });
        })
        .finally(() => (this.isLoading = false));
    },
    findMatchingSeasonInlandsflug(tag, abflughafen) {
      if (this.gueltigeSaisonInlandsfluege) {
        const matchingInlandflug = this.gueltigeSaisonInlandsfluege.find(
          inlf => inlf.tag === tag && inlf.abflughafenIataCode === abflughafen
        );
        return matchingInlandflug ?? null;
      } else return null;
    },
    formatParamsMetadata() {
      const fieldsToSkipFlugdaten = ['TH Langstrecke', 'Verkehrstag Hin'];
      return {
        ...this.reisedaten.reduce((acc, curr) => {
          if (!curr.paramLabel || fieldsToSkipFlugdaten.includes(curr.label?.[0])) return acc;
          if (curr.paramLabel === 'releasefrist') {
            acc[curr.paramLabel] = [...curr.valueEdited];
          } else if (Array.isArray(curr.paramLabel)) {
            acc[curr.paramLabel[0]] = curr.valueEdited[0];
            acc[curr.paramLabel[1]] = curr.valueEdited[1];
            return acc;
          }
          acc[curr.paramLabel] = curr.valueEdited;
          return acc;
        }, {}),
        ...this.flugdaten.reduce((acc, curr) => {
          if (!curr.paramLabel || fieldsToSkipFlugdaten.includes(curr.label?.[0])) return acc;
          if (Array.isArray(curr.paramLabel)) {
            acc[curr.paramLabel[0]] = curr.valueEdited[0];
            acc[curr.paramLabel[1]] = curr.valueEdited[1];
            return acc;
          } else if (
            curr.paramLabel === 'restriktionenHinRollover' ||
            curr.paramLabel === 'restriktionenRueckRollover'
          ) {
            acc[curr.paramLabel] = curr.valueEdited == null ? curr.valueEdited : +curr.valueEdited;
            return acc;
          }
          acc[curr.paramLabel] = curr.valueEdited;
          return acc;
        }, {}),
        // Die Country Manager sind in der Sidebar nicht bearbeitbar und werden unverändert mitgeschickt
        countryManagerKuerzel: countryManagerKuerzelParam(this.activeMetadata),
      };
    },
    // Schlaegt ein Teilbereich fehl, bricht das Speichern ab, ohne die Sidebar im Ladezustand zu lassen
    async saveChanges() {
      try {
        await this.saveChangesTeilbereiche();
      } catch (error) {
        console.error('Speichern der Terminplanung fehlgeschlagen:', error);
        if (!error?.isAxiosError) {
          this.toast('Speichern fehlgeschlagen.', { variant: 'danger' });
        }
      } finally {
        this.isLoading = false;
      }
    },
    async saveChangesTeilbereiche() {
      this.isLoading = true;

      // Change Metadata if it was changed
      if (!this.noMetadataChanges) {
        const timeOnlyParams = [
          'fruehesteAnkunft',
          'spaetesteAnkunft',
          'fruehesterAbflug',
          'spaetesterAbflug',
        ];
        const params = this.formatParamsMetadata();
        const formattedParams = {
          [this.activeReisetermin.startdatum]: Object.keys(params).reduce((acc, curr) => {
            if (timeOnlyParams.includes(curr)) {
              // add seconds so that .net accepts this value as TimeOnly
              acc[curr] = params[curr]?.length === 5 ? params[curr] + ':00' : params[curr];
            } else {
              acc[curr] = params[curr];
            }
            return acc;
          }, {}),
        };
        const reisekuerzel = this.activeReisetermin.reiseterminkuerzel.substring(0, 6);

        await this.$store
          .dispatch(EDIT_REISETERMIN_METADATA_MULTIPLE, { formattedParams, reisekuerzel })
          .then(async res => {
            this.$emit('reiseterminMetadataBearbeitet', res.data.result.value);
          });
      }
      // Change Reisetermin Data if it was changed
      if (!this.noReiseterminDataChanges) {
        await this.saveChangesReisetermin();
      }
      // If there is an inlandsflug that is ready to save
      if (!this.noReadyLocalInlandsflug) {
        this.isLoading = true;
        const timeOnlyParams = ['spaetesteAnkunft', 'fruehesterAbflug'];
        const params = Object.keys(this.localInlandsflug).reduce((acc, curr) => {
          if (curr === 'isOnlyLocal' || curr === 'id' || curr === 'valuesBeforeEdit') return acc;
          else if (['zielflughafenIataCode', 'abflughafenIataCode'].includes(curr)) {
            acc[curr] = Array.isArray(this.localInlandsflug[curr])
              ? this.localInlandsflug[curr].join()
              : this.localInlandsflug[curr];
          } else if (timeOnlyParams.includes(curr) && this.localInlandsflug[curr]) {
            // add seconds so that .net accepts this value as TimeOnly
            acc[curr] = this.localInlandsflug[curr] + ':00';
          } else {
            acc[curr] = this.localInlandsflug[curr];
          }
          return acc;
        }, {});
        apiService
          .post(
            `/Reise/${this.activeReisetermin.reise.reisekuerzel}/metadata/${this.activeReisetermin.startdatum}/inlandsflug/reisetermin`,
            params
          )
          .then(resp => {
            const newInlandsflug = resp.data.result.value;
            // insert new inlandsflug
            this.removeLocalInlandsfluege();
            this.addNewInlandsflug(newInlandsflug);
            this.$emit('createdInlandsflug', {
              reiseterminkuerzel: this.activeReisetermin.reiseterminkuerzel,
              newInlandsflug,
            });
          })
          .catch(() => {
            this.removeLocalInlandsfluege();
          });
      }
      // Change Inlandsflug Data if it was changed
      if (!this.noInlandsflugChanges) {
        const changedInlandsfluege = this.inlandsfluegeChanged;
        for (var inlandsflug of changedInlandsfluege) {
          await this.saveChangesInlandsflug(inlandsflug);
        }
      }
      if (!this.noZimmerkontingenteChanges) {
        await this.$refs.zimmerkontingentePlan?.save();
      }
      let reiseterminAngelegt = false;
      if (!this.noCmChanges) {
        await this.$refs.cmRueckmeldung?.save();
        reiseterminAngelegt = true;
      }
      await this.loadFullReisetermin(this.reiseterminId);
      this.toast('Erfolgreich bearbeitet.');
      if (reiseterminAngelegt) {
        this.$emit('reiseterminAngelegt');
      }

      this.rebuildAbschnitte();
      this.cmHasChanges = false;
    },
    removeLocalInlandsfluege() {
      this.inlandsfluege = this.inlandsfluege.filter(inlandsflug => !inlandsflug?.isOnlyLocal);
    },
    addNewInlandsflug(inlandsflug) {
      this.inlandsfluege = [
        {
          ...inlandsflug,
          spaetesteAnkunft: inlandsflug.spaetesteAnkunft?.substring(0, 5),
          fruehesterAbflug: inlandsflug.fruehesterAbflug?.substring(0, 5),
          matchingSeasonInlandsflug: this.findMatchingSeasonInlandsflug(
            inlandsflug.tag,
            inlandsflug.abflughafenIataCode
          ),
          valuesBeforeEdit: { ...inlandsflug },
        },
        ...this.inlandsfluege,
      ].sort((a, b) => (a.tag === b.tag ? 0 : a.tag > b.tag ? 1 : -1));
      this.$emit('newInlandsflug', inlandsflug);
    },
    saveChangesInlandsflug(inlandsflug) {
      const reisekuerzel = this.activeReisetermin.reiseterminkuerzel.substring(0, 6);
      const { startdatum } = this.activeReisetermin;
      return apiService
        .put(
          `Reise/${reisekuerzel}/metadata/${startdatum}/inlandsflug/reisetermin?inlandsflugId=${inlandsflug.id}`,
          {
            tag: inlandsflug.tag,
            einkauf: inlandsflug.einkauf,
            fruehesterAbflug: inlandsflug.fruehesterAbflug
              ? inlandsflug.fruehesterAbflug.substring(0, 5) + ':00'
              : inlandsflug.fruehesterAbflug,
            spaetesteAnkunft: inlandsflug.spaetesteAnkunft
              ? inlandsflug.spaetesteAnkunft.substring(0, 5) + ':00'
              : inlandsflug.spaetesteAnkunft,
            minimaleUmsteigezeit: inlandsflug.minimaleUmsteigezeit,
            zielflughafenIataCode: Array.isArray(inlandsflug.zielflughafenIataCode)
              ? inlandsflug.zielflughafenIataCode.join()
              : inlandsflug.zielflughafenIataCode,
            abflughafenIataCode: Array.isArray(inlandsflug.abflughafenIataCode)
              ? inlandsflug.abflughafenIataCode.join()
              : inlandsflug.abflughafenIataCode,
          }
        )
        .then(res => {
          this.toast('Erfolgreich bearbeitet.');
          this.$emit('inlandsflugBearbeitet', {
            reiseterminkuerzel: this.activeReisetermin.reiseterminkuerzel,
            inlandsflug: { ...res.data.result.value },
          });
        });
    },
    openModalWithData() {
      this.prefixedVerlinkungen = [
        {
          id: 0,
          typ: 'Reisetermin',
          artifactId: this.activeReisetermin.id,
          artifactText: this.activeReisetermin.reiseterminkuerzel,
        },
      ];
      this.$emit('showModalAufgabeEdit', this.prefixedVerlinkungen);
    },
    onShow() {
      this.activeTab = 'Allgemein';
      this.addKeyEventListener();
      this.initInlandsfluegeAndReiseterminData();
    },
    onHidden() {
      this.$emit('sidebarClosed');
      this.removeKeyEventListener();
      this.deleteCollapseFromURL();
    },
    goUpOneRow() {
      if (!this.isLoading) {
        this.$emit('rowSelectionChanged', 'up');
      }
    },
    goDownOneRow() {
      if (!this.isLoading) {
        this.$emit('rowSelectionChanged', 'down');
      }
    },
    handleKeyPress($event) {
      if ($event.key === 'ArrowLeft') {
        this.goUpOneRow();
      } else if ($event.key === 'ArrowRight') {
        this.goDownOneRow();
      }
    },
    addKeyEventListener() {
      window.addEventListener('keydown', this.handleKeyPress);
    },
    removeKeyEventListener() {
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    initInlandsfluegeAndReiseterminData() {
      // Fallback auf ein leeres Array, damit inlandsfluege nie undefiniert ist
      this.inlandsfluege = (this.activeMetadata?.inlandsfluege ?? [])
        .slice()
        .map(inlf => ({
          ...inlf,
          matchingSeasonInlandsflug: this.findMatchingSeasonInlandsflug(inlf.tag, inlf.abflughafenIataCode),
          valuesBeforeEdit: { ...inlf },
        }))
        .sort((a, b) => (a.tag === b.tag ? 0 : a.tag > b.tag ? 1 : -1));
    },
    saveChangesReisetermin() {
      const reisekuerzel = this.activeReisetermin?.reiseterminkuerzel.substring(0, 6);
      const startdatum = this.activeReisetermin?.startdatum;
      const istZusatztermin = this.reisetermindaten.find(item => item.label === 'Zusatztermin')?.valueEdited;
      const istInKlaerung = this.reisetermindaten.find(item => item.label === 'In Klärung')?.valueEdited;
      const [thLangstrecke, thInlandsflug] = this.flugdaten.find(
        item => item.label?.[0] === 'TH Langstrecke'
      )?.valueEdited;
      return apiService
        .put(`Reise/${reisekuerzel}/Reisetermin/${startdatum}/`, {
          thInlandsflug,
          thLangstrecke,
          reisekuerzel,
          startdatum,
          istZusatztermin,
          istInKlaerung,
        })
        .then(() => {
          this.toast('Erfolgreich bearbeitet.');
          this.$emit(
            'reiseterminBearbeitet',
            this.activeReisetermin.reiseterminkuerzel,
            thLangstrecke,
            thInlandsflug,
            istZusatztermin,
            istInKlaerung
          );
          this.activeReisetermin = {
            ...this.activeReisetermin,
            thLangstrecke,
            thInlandsflug,
            istZusatztermin,
            istInKlaerung,
          };
        });
    },
    onClickDeleteReisetermin(hide) {
      this.pendingReiseterminHide = hide;
      this.$bvModal.show('modalReiseterminLoeschen');
    },
    confirmDeleteReisetermin() {
      this.$bvModal.hide('modalReiseterminLoeschen');

      this.$emit('reiseterminLoeschen');

      if (typeof this.pendingReiseterminHide === 'function') {
        this.pendingReiseterminHide();
      }
      this.pendingReiseterminHide = null;
    },
    async loadVerlauf(reiseterminId, startdatum, reiseId) {
      if (reiseterminId && startdatum && reiseId) {
        try {
          const [reiseterminDatenHistoryResponse, DatenHistoryResponse] = await Promise.all([
            apiService.get(`/history/Reisetermine/${reiseterminId}`),
            apiService.get(`/history/ReiseterminMetadata/${reiseId}/${startdatum}`),
          ]);

          const reiseterminDatenHistory =
            reiseterminDatenHistoryResponse.status === 200 ? reiseterminDatenHistoryResponse.data.result : {};

          const DatenHistory = DatenHistoryResponse.status === 200 ? DatenHistoryResponse.data.result : {};

          const combinedHistory = { ...reiseterminDatenHistory, ...DatenHistory };

          this.verlaufData = combinedHistory;
        } catch (error) {
          console.error('Fehler beim Laden der Verlaufsdaten:', error);
          this.verlaufData = {};
        }
      }
    },
  },
};
</script>

<style scoped>
:deep(.b-sidebar) {
  border-radius: var(--radius-lg) 0 0 var(--radius-lg);
}
.sidebar-content {
  width: 100%;
  padding: 0 24px;
}
.sidebar-country-manager-bereich {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sidebar-tabs {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}
.sidebar-tab {
  padding: 8px 2px;
  border: 0;
  background: none;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--text-muted);
  cursor: pointer;
}
.sidebar-tab:focus {
  outline: none;
}
.sidebar-tab:hover {
  color: var(--text-body);
}
.sidebar-tab--active {
  color: var(--primary);
  box-shadow: inset 0 -2px 0 var(--primary);
}
</style>
